import { useEffect, useState, useCallback, type ReactNode } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Check, X, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

type Info = {
  business_name: string | null;
  collaborator_email: string | null;
  status: string;
  approval_expired: boolean;
};

type PageState =
  | { kind: "loading" }
  | { kind: "ready"; info: Info }
  | { kind: "done"; status: string; businessName: string | null }
  | { kind: "error"; message: string };

// A newly-issued request can briefly miss on its first read (for example while
// an asynchronously replicated API read catches up). Keep that transient state
// behind the loading screen instead of asking the owner to refresh manually.
// The retry is deliberately limited to the read-only `info` decision below.
const INFO_LOOKUP_RETRY_DELAYS_MS = [250, 750, 1_500] as const;

// Owner-facing approval screen reached from the emailed link:
//   /collaborator-access/approve?grant=<id>&token=<raw>
// Wrapped in <ProtectedRoute>, so the owner must be signed in. The
// collaborator-respond edge fn validates the token + that the caller is the
// business owner before any state change. Approving adds the Dibs operator as a
// full-access collaborator with access until the owner revokes it.
export default function CollaboratorAccessApprove() {
  const [params] = useSearchParams();
  const grantId = params.get("grant") || "";
  const token = params.get("token") || "";

  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [submitting, setSubmitting] = useState<null | "approve" | "deny">(null);

  const extractError = useCallback(
    async (error: unknown, fallback: string): Promise<string> => {
      try {
        const ctx = (error as { context?: Response }).context;
        if (ctx) {
          const body = await ctx.json();
          if (body?.error) return body.error as string;
        }
      } catch {
        /* keep fallback */
      }
      return fallback;
    },
    []
  );

  // Cold email click: the session restored from storage may have an expired (or
  // about-to-expire) access token, and supabase.auth.getSession() does NOT force
  // a refresh, so an immediate invoke fires with a stale JWT and the edge fn
  // replies "Not authenticated" even though the owner is signed in. Proactively
  // refresh when the token is missing or within 60s of expiry.
  const ensureFreshSession = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    let session = data?.session ?? null;
    const expMs = (session?.expires_at ?? 0) * 1000;
    if (session && expMs - Date.now() < 60_000) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed?.session) session = refreshed.session;
    }
    return session;
  }, []);

  // Single entry point for the collaborator-respond invoke. Ensures a fresh
  // token first, retries one cold-load auth race, and gives a newly-created
  // request a short bounded window to become readable before surfacing a 404.
  // Returns the already-extracted error message so callers don't double-consume
  // the response body.
  const callRespond = useCallback(
    async (decision: "info" | "approve" | "deny") => {
      await ensureFreshSession();

      const invokeOnce = async () => {
        const res = await supabase.functions.invoke("collaborator-respond", {
          body: { grantId, token, decision },
        });
        const status = (res.error as { context?: Response } | null)?.context
          ?.status ?? null;
        const raw = res.error ? await extractError(res.error, "") : "";
        return { data: res.data, error: res.error, raw, status };
      };

      let result = await invokeOnce();
      let canRetryAuth = true;
      let missingRequestAttempt = 0;

      while (result.error) {
        if (
          canRetryAuth &&
          /not authenticated|missing authorization/i.test(result.raw)
        ) {
          canRetryAuth = false;
          await supabase.auth.refreshSession();
          await new Promise((resolve) => window.setTimeout(resolve, 400));
          result = await invokeOnce();
          continue;
        }

        const retryDelay =
          INFO_LOOKUP_RETRY_DELAYS_MS[missingRequestAttempt];
        const transientMissingRequest =
          decision === "info" &&
          result.status === 404 &&
          /^request not found$/i.test(result.raw.trim());

        if (transientMissingRequest && retryDelay !== undefined) {
          missingRequestAttempt += 1;
          await new Promise((resolve) => window.setTimeout(resolve, retryDelay));
          result = await invokeOnce();
          continue;
        }

        break;
      }

      return result;
    },
    [grantId, token, ensureFreshSession, extractError]
  );

  const load = useCallback(async () => {
    if (!grantId || !token) {
      setState({ kind: "error", message: "This approval link is invalid." });
      return;
    }
    try {
      const session = await ensureFreshSession();
      const signedInEmail = session?.user?.email ?? null;
      const { data, error, raw } = await callRespond("info");
      if (error) {
        // The edge fn 403s when the signed-in user isn't the business owner.
        // Most often because the operator opened their own request, or the
        // owner is logged into a different account. Tell them exactly that.
        const wrongAccount = /not authorized to respond/i.test(raw);
        setState({
          kind: "error",
          message: wrongAccount
            ? `This request can only be approved by the account that owns this business.${
                signedInEmail ? ` You're signed in as ${signedInEmail}.` : ""
              } Sign out and sign in as the owner account, then open this link again.`
            : raw || "We couldn't load this request.",
        });
        return;
      }
      if (!data?.success) {
        setState({
          kind: "error",
          message: data?.error || "We couldn't load this request.",
        });
        return;
      }
      setState({
        kind: "ready",
        info: {
          business_name: data.business_name,
          collaborator_email: data.collaborator_email,
          status: data.status,
          approval_expired: data.approval_expired,
        },
      });
    } catch (e) {
      setState({
        kind: "error",
        message: e instanceof Error ? e.message : "Something went wrong.",
      });
    }
  }, [grantId, token, ensureFreshSession, callRespond]);

  useEffect(() => {
    load();
  }, [load]);

  const respond = useCallback(
    async (decision: "approve" | "deny") => {
      setSubmitting(decision);
      try {
        const { data, error, raw } = await callRespond(decision);
        if (error) {
          setState({
            kind: "error",
            message: raw || "We couldn't record your response.",
          });
          return;
        }
        const businessName =
          state.kind === "ready" ? state.info.business_name : data?.business_name ?? null;
        if (!data?.success) {
          setState({
            kind: "done",
            status: data?.status || "unavailable",
            businessName,
          });
          return;
        }
        setState({ kind: "done", status: data.status, businessName });
      } catch (e) {
        setState({
          kind: "error",
          message: e instanceof Error ? e.message : "Something went wrong.",
        });
      } finally {
        setSubmitting(null);
      }
    },
    [callRespond, state]
  );

  const Shell = ({ children }: { children: ReactNode }) => (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-md bg-background rounded-xl border shadow-sm p-8 text-center">
        {children}
      </div>
    </div>
  );

  if (state.kind === "loading") {
    return (
      <Shell>
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">Loading request…</p>
      </Shell>
    );
  }

  if (state.kind === "error") {
    return (
      <Shell>
        <AlertTriangle className="h-10 w-10 mx-auto text-destructive" />
        <h1 className="mt-4 text-lg font-semibold">Can't open this request</h1>
        <p className="mt-2 text-muted-foreground">{state.message}</p>
        <Link to="/" className="mt-6 inline-block text-sm text-primary underline">
          Go to dashboard
        </Link>
      </Shell>
    );
  }

  if (state.kind === "done") {
    const approved = state.status === "active";
    const denied = state.status === "revoked" || state.status === "denied";
    return (
      <Shell>
        {approved ? (
          <Check className="h-10 w-10 mx-auto text-green-600" />
        ) : (
          <X className="h-10 w-10 mx-auto text-muted-foreground" />
        )}
        <h1 className="mt-4 text-lg font-semibold">
          {approved
            ? "Collaborator access approved"
            : denied
            ? "Request denied"
            : `Request ${state.status}`}
        </h1>
        <p className="mt-2 text-muted-foreground">
          {approved
            ? `The Dibs team can now help set up ${
                state.businessName || "your account"
              }. Access stays active until you remove it — you can revoke it anytime from Account Settings.`
            : "No access was granted. You can close this page."}
        </p>
        <Link to="/" className="mt-6 inline-block text-sm text-primary underline">
          Go to dashboard
        </Link>
      </Shell>
    );
  }

  // ready
  const { info } = state;
  const notPending = info.status !== "pending" || info.approval_expired;

  return (
    <Shell>
      <ShieldCheck className="h-10 w-10 mx-auto text-primary" />
      <h1 className="mt-4 text-lg font-semibold">Approve Dibs setup help</h1>
      <p className="mt-2 text-muted-foreground">
        The Dibs team would like to be added as a temporary collaborator on{" "}
        <strong>{info.business_name || "your account"}</strong> to help set up your
        AI agent, knowledge base and business settings.
      </p>
      <p className="mt-3 text-sm text-muted-foreground">
        If you approve, a Dibs specialist can access your dashboard to help with
        setup. Access stays active <strong>until you remove it</strong> — you only
        approve once. You can revoke access at any time from Account Settings.
      </p>

      {notPending ? (
        <div className="mt-6 rounded-lg bg-muted p-4 text-sm text-muted-foreground">
          {info.approval_expired
            ? "This approval link has expired."
            : `This request is already ${info.status}.`}
          <Link to="/" className="mt-3 block text-primary underline">
            Go to dashboard
          </Link>
        </div>
      ) : (
        <div className="mt-6 flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            disabled={submitting !== null}
            onClick={() => respond("deny")}
          >
            {submitting === "deny" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Deny"
            )}
          </Button>
          <Button
            className="flex-1"
            disabled={submitting !== null}
            onClick={() => respond("approve")}
          >
            {submitting === "approve" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Approve"
            )}
          </Button>
        </div>
      )}
    </Shell>
  );
}
